<form [formGroup]="formGroup">
  <div class="d-flex flex-column" [style.gap.rem]="2">
    <div class="enrolment-copy" i18n>
      You must be at least 18 years old to join SPP. Fill in the required details and upload a copy of your passport,
      driver’s license, or birth certificate to verify your age.
    </div>
    <ng-container *ngIf="!(isLoading$ | async); else loading">
      <div class="form-grid">
        <div class="d-flex flex-column gap-2">
          <label [for]="'firstName'" class="form-input-label" i18n>First Name</label>
          <app-text-input
            formControlName="firstName"
            controlId="firstName"
            [error]="formSubmitted && formGroup.controls.firstName.invalid" />
          <app-form-error
            *ngIf="formSubmitted && formGroup.controls.firstName.invalid"
            [errors]="formGroup.controls.firstName.errors" />
        </div>
        <div class="d-flex flex-column gap-2">
          <label [for]="'middleName'" class="form-input-label" i18n>Middle Name</label>
          <app-text-input
            formControlName="middleName"
            controlId="middleName"
            [error]="formSubmitted && formGroup.controls.middleName.invalid" />
          <div class="form-input-hint" i18n>Optional</div>
        </div>
        <div class="d-flex flex-column gap-2">
          <label [for]="'lastName'" class="form-input-label" i18n>Last Name</label>
          <app-text-input
            formControlName="lastName"
            controlId="lastName"
            [error]="formSubmitted && formGroup.controls.lastName.invalid" />
          <app-form-error
            *ngIf="formSubmitted && formGroup.controls.lastName.invalid"
            [errors]="formGroup.controls.lastName.errors" />
        </div>
        <div class="d-flex flex-column gap-2">
          <label [for]="'preferredName'" class="form-input-label" i18n>Preferred Name</label>
          <app-text-input
            formControlName="preferredName"
            controlId="preferredName"
            [error]="formSubmitted && formGroup.controls.preferredName.invalid" />
          <div class="form-input-hint" i18n>Optional</div>
        </div>
        <div class="d-flex flex-column gap-2">
          <label [for]="'birthDate'" class="form-input-label" i18n>Date of Birth</label>
          <app-datepicker-input
            formControlName="birthDate"
            controlId="birthDate"
            [maxDate]="today"
            [error]="formSubmitted && formGroup.controls.birthDate.invalid" />
          <app-form-error
            *ngIf="formSubmitted && formGroup.controls.birthDate.invalid"
            [customErrorMessages]="customErrorMessages.birthDate"
            [errors]="formGroup.controls.birthDate.errors" />
        </div>
        <div class="d-flex flex-column gap-2">
          <label [for]="'socialInsuranceNumber'" class="form-input-label" i18n>Social Insurance Number</label>
          <app-sin-input
            formControlName="socialInsuranceNumber"
            controlId="socialInsuranceNumber"
            [error]="formSubmitted && formGroup.controls.socialInsuranceNumber.invalid" />
          <app-form-error
            *ngIf="formSubmitted && formGroup.controls.socialInsuranceNumber.invalid"
            [customErrorMessages]="customErrorMessages.socialInsuranceNumber"
            [errors]="formGroup.controls.socialInsuranceNumber.errors" />
        </div>
        <div class="d-flex flex-column gap-2">
          <label [for]="'genderId'" class="form-input-label" i18n>Gender at Birth</label>
          <app-dropdown-input
            formControlName="genderId"
            controlId="genderId"
            [placeholder]="''"
            [dropdowns]="genderDropdowns$ | async"
            [error]="formSubmitted && formGroup.controls.genderId.invalid" />
          <app-form-error
            *ngIf="formSubmitted && formGroup.controls.genderId.invalid"
            [errors]="formGroup.controls.genderId.errors" />
        </div>
        <div class="d-flex flex-column gap-2">
          <label [for]="'pronounId'" class="form-input-label" i18n>Preferred Pronouns</label>
          <app-dropdown-input
            formControlName="pronounId"
            controlId="pronounId"
            [placeholder]="''"
            [dropdowns]="pronounDropdowns$ | async"
            [error]="formSubmitted && formGroup.controls.pronounId.invalid" />
          <app-form-error
            *ngIf="formSubmitted && formGroup.controls.pronounId.invalid"
            [errors]="formGroup.controls.pronounId.errors" />
        </div>
      </div>
      <div class="d-flex flex-column gap-2">
        <label class="form-input-label mb-1" [for]="'files'" i18n> Proof of Age </label>
        <app-file-upload-field
          formControlName="files"
          [control]="formGroup.controls.files"
          [inputName]="'files'"
          [multiple]="true"
          [accept]="['image/png', 'image/jpeg', 'application/pdf']"
          [maxFileSizeInMb]="3">
          <ng-container input-copy>
            <img height="24" [src]="'assets/icons/blue/UploadFile.svg'" alt="Upload File" i18n-alt />
            <app-file-uploader-text
              instructionText="Upload a file or drag and drop a .png, .jpeg, or .pdf"
              i18n-instructionText
              descriptionText="Up to 3 MB"
              i18n-descriptionText />
          </ng-container>
          <app-form-error
            input-error
            class="d-flex mt-2"
            *ngIf="formSubmitted && formGroup.controls.files.invalid"
            [errors]="formGroup.controls.files.errors" />
        </app-file-upload-field>
        <span class="form-input-hint" i18n>Upload a copy of your passport, driver’s license, or birth certificate</span>
      </div>
      <div class="third-party-contributions d-flex flex-column gap-3">
        <div class="d-flex justify-content-between align-items-start gap-3">
          <div class="d-flex flex-column gap-1">
            <span class="third-party-contributions-title" i18n>Third Party Contributions</span>
            <p class="enrolment-copy mb-0" i18n>Allow other people to contribute to my account.</p>
          </div>
          <app-toggle-switch-input
            formControlName="allowThirdPartyContributions"
            controlId="allowThirdPartyContributions"
            ariaLabel="Third Party Contributions"
            i18n-ariaLabel />
        </div>
        <p class="enrolment-copy mb-0" i18n>
          Enabling Third Party Contributions allows other people who know your SPP member ID to make contributions to
          your account. Anything they contribute is allocated to your RRSP contribution room, the same as a contribution
          you make yourself.
        </p>
      </div>
      <app-primary-button [size]="BaseButtonSizeEnum.md" (buttonClicked)="submit()" class="submit-button" i18n>
        Continue
      </app-primary-button>
    </ng-container>
  </div>
</form>
<ng-template #loading>
  <div class="d-flex flex-column gap-4">
    <div class="form-grid">
      <div class="d-flex flex-column gap-2" *appRepeat="8">
        <lib-loading-shimmer [height]="'1.25rem'" [width]="'5rem'" />
        <lib-loading-shimmer [height]="'3.5rem'" [width]="'100%'" />
      </div>
    </div>
  </div>
  <lib-loading-shimmer [height]="'11.5rem'" [width]="'100%'" />
</ng-template>
